<template>
  <section class="mx-auto flex min-h-[70vh] max-w-5xl flex-col gap-8 px-6 py-10">
    <div v-if="loading" class="space-y-4">
      <div class="aspect-[16/8] rounded-3xl border border-white/10 bg-white/10 animate-pulse"></div>
      <div class="rounded-3xl border border-white/10 bg-midnight-800/60 p-6 animate-pulse">
        <div class="h-4 w-28 rounded bg-white/10"></div>
        <div class="mt-4 h-8 w-2/3 rounded bg-white/10"></div>
        <div class="mt-4 h-4 w-full rounded bg-white/10"></div>
      </div>
    </div>

    <template v-else-if="meet">
      <div class="overflow-hidden rounded-[2rem] border border-white/10 bg-midnight-800/60 shadow-glow">
        <button
          type="button"
          class="block aspect-[16/8] w-full overflow-hidden bg-midnight-900/70"
          @click="openImageLightbox"
        >
          <img
            v-if="meet.coverImage"
            :src="meet.coverImage"
            :alt="meet.title"
            class="h-full w-full object-cover"
            :style="{ objectPosition: meet.coverPosition }"
          />
          <div v-else class="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.18),_transparent_36%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(0,0,0,0.12))] text-xs uppercase tracking-[0.35em] text-steel-400">
            {{ $t('meetDetailPage.eventFallback') }}
          </div>
        </button>

        <div class="space-y-6 p-6 sm:p-8">
          <div class="flex flex-wrap items-center gap-2">
            <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-steel-300">{{ meet.eventTypeLabel }}</span>
            <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-steel-300">{{ meet.statusLabel }}</span>
            <span v-if="meet.isHost" class="rounded-full border border-neon-400/20 bg-neon-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-neon-300">{{ $t('meetDetailPage.youHost') }}</span>
          </div>

          <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div class="max-w-3xl">
              <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('meetDetailPage.eyebrow') }}</p>
              <h1 class="mt-2 font-display text-3xl font-semibold text-white sm:text-4xl">{{ meet.title }}</h1>
              <p class="mt-4 text-base leading-7 text-steel-200/90">{{ meet.description || $t('meetDetailPage.noDescription') }}</p>
            </div>

            <div class="flex flex-wrap gap-3">
              <button
                v-if="meet.isHost"
                type="button"
                class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
                @click="openEditModal"
              >
                {{ $t('meetDetailPage.editEvent') }}
              </button>
              <a
                :href="meet.mapsUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
              >
                {{ $t('meetDetailPage.openMaps') }}
              </a>
              <button
                v-if="!meet.isHost && !meet.isGoing"
                type="button"
                class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
                :disabled="busy"
                @click="joinMeet"
              >
                {{ busy ? $t('meetDetailPage.updating') : $t('meetDetailPage.rsvpGoing') }}
              </button>
              <button
                v-else-if="!meet.isHost && meet.isGoing"
                type="button"
                class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-70"
                :disabled="busy"
                @click="leaveMeet"
              >
                {{ busy ? $t('meetDetailPage.updating') : $t('meetDetailPage.cancelRsvp') }}
              </button>
              <button
                v-if="meet.isHost && meet.status !== 'cancelled'"
                type="button"
                class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-rose-100 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-70"
                :disabled="busy"
                @click="cancelMeet"
              >
                {{ busy ? $t('meetDetailPage.updating') : $t('meetDetailPage.cancelEvent') }}
              </button>
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-3">
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
              <p class="text-[10px] uppercase tracking-[0.28em] text-steel-400">{{ $t('meetDetailPage.location') }}</p>
              <p class="mt-2 text-sm font-semibold text-white">{{ meet.locationLabel }}</p>
              <p class="mt-1 text-sm text-steel-300">{{ meet.venue || $t('meetDetailPage.venueTbd') }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
              <p class="text-[10px] uppercase tracking-[0.28em] text-steel-400">{{ $t('meetDetailPage.starts') }}</p>
              <p class="mt-2 text-sm font-semibold text-white">{{ meet.startsAtLabel }}</p>
              <p class="mt-1 text-sm text-steel-300">{{ meet.endsAtLabel }}</p>
            </div>
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
              <p class="text-[10px] uppercase tracking-[0.28em] text-steel-400">{{ $t('meetDetailPage.attendance') }}</p>
              <p class="mt-2 text-sm font-semibold text-white">{{ attendeeCountLabel }} {{ $t('meetsPage.going') }}</p>
              <p class="mt-1 text-sm text-steel-300">{{ meet.capacityLabel ? `${$t('meetsPage.capacity')} ${meet.capacityLabel}` : $t('meetDetailPage.noAttendeeCap') }}</p>
            </div>
          </div>

          <div class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/50 px-4 py-3">
            <div class="h-12 w-12 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img v-if="meet.hostAvatar" :src="meet.hostAvatar" alt="" class="h-full w-full object-cover" referrerpolicy="no-referrer" crossorigin="anonymous" />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ meet.hostInitials }}</div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <router-link :to="`/u/${meet.hostHandle}`" class="truncate text-sm font-semibold text-white hover:text-neon-400">{{ meet.hostName }}</router-link>
                <verified-badge v-if="meet.hostVerified" size="xs" />
              </div>
              <p class="text-[10px] uppercase tracking-[0.24em] text-steel-400">@{{ meet.hostHandle }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div class="space-y-6">
        <div class="rounded-3xl border border-white/10 bg-midnight-800/60 p-6 shadow-glow">
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('meetDetailPage.attendees') }}</p>
              <h2 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('meetDetailPage.whoIsGoing') }}</h2>
            </div>
            <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-steel-300">{{ attendeeCountLabel }}</span>
          </div>

          <div v-if="attendees.length" class="mt-6 space-y-3">
            <router-link
              v-for="attendee in attendees"
              :key="attendee.id"
              :to="`/u/${attendee.handle}`"
              class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/55 px-3 py-3 transition hover:border-white/20"
            >
              <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                <img v-if="attendee.avatar" :src="attendee.avatar" alt="" class="h-full w-full object-cover" referrerpolicy="no-referrer" crossorigin="anonymous" />
                <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ attendee.initials }}</div>
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-1.5">
                  <p class="truncate text-sm text-white">{{ attendee.fullName }}</p>
                  <verified-badge v-if="attendee.verified" size="xs" />
                </div>
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ attendee.handle }}</p>
              </div>
            </router-link>
          </div>
          <p v-else class="mt-6 text-sm text-steel-300">{{ $t('meetDetailPage.noAttendees') }}</p>
        </div>

        <div class="overflow-hidden rounded-3xl border border-white/10 bg-midnight-800/60 shadow-glow">
          <div class="border-b border-white/10 px-6 py-5">
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('meetDetailPage.map') }}</p>
            <h2 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('meetDetailPage.meetingPoint') }}</h2>
            <p class="mt-2 text-sm text-steel-300">{{ $t('meetDetailPage.mapDescription') }}</p>
          </div>
          <div class="aspect-[4/3] bg-midnight-900/70">
            <iframe
              :src="meet.embedMapsUrl"
              class="h-full w-full border-0"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              allowfullscreen
              title="Event map"
            ></iframe>
          </div>
        </div>
        </div>

        <div class="rounded-3xl border border-white/10 bg-midnight-800/60 p-6 shadow-glow">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('meetDetailPage.comments') }}</p>
            <h2 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('meetDetailPage.quickNotes') }}</h2>
            <p class="mt-2 text-sm text-steel-300">{{ $t('meetDetailPage.commentsDescription') }}</p>
          </div>

          <form class="mt-6 space-y-3" @submit.prevent="addComment">
            <textarea
              v-model.trim="newComment"
              rows="3"
              maxlength="240"
              class="w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              :placeholder="$t('meetDetailPage.commentPlaceholder')"
            ></textarea>
            <div class="flex items-center justify-between gap-3">
              <p class="text-xs text-steel-400">{{ newComment.length }}/240</p>
              <button
                type="submit"
                class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
                :disabled="commentBusy || !newComment"
              >
                {{ commentBusy ? $t('meetDetailPage.posting') : $t('meetDetailPage.postComment') }}
              </button>
            </div>
          </form>

          <div v-if="comments.length" class="mt-6 space-y-4">
            <div v-for="comment in comments" :key="comment.id" class="rounded-2xl border border-white/10 bg-midnight-900/55 p-4">
              <div class="flex items-center gap-3">
                <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img v-if="comment.authorAvatar" :src="comment.authorAvatar" alt="" class="h-full w-full object-cover" referrerpolicy="no-referrer" crossorigin="anonymous" />
                  <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ comment.authorInitials }}</div>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5">
                    <router-link :to="`/u/${comment.authorHandle}`" class="truncate text-sm font-semibold text-white hover:text-neon-400">{{ comment.authorName }}</router-link>
                    <verified-badge v-if="comment.authorVerified" size="xs" />
                  </div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ comment.authorHandle }}</p>
                </div>
                <p class="text-xs text-steel-400">{{ comment.createdLabel }}</p>
              </div>
              <p class="mt-3 whitespace-pre-line text-sm leading-6 text-steel-200">{{ comment.content }}</p>
              <div v-if="comment.user_id === userId" class="mt-3 flex justify-end">
                <button type="button" class="text-xs uppercase tracking-[0.28em] text-steel-400 transition hover:text-white" @click="deleteComment(comment.id)">
                  {{ $t('meetDetailPage.delete') }}
                </button>
              </div>
            </div>
          </div>
          <p v-else class="mt-6 text-sm text-steel-300">{{ $t('meetDetailPage.noComments') }}</p>
        </div>
      </div>

      <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{{ errorMessage }}</p>
    </template>

    <p v-else class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{{ $t('meetDetailPage.notFound') }}</p>

    <div v-if="showImageLightbox" class="fixed inset-0 z-[140] flex items-center justify-center bg-black/80 px-6" @click.self="closeImageLightbox">
      <div class="relative w-full max-w-5xl">
        <button
          type="button"
          class="absolute right-0 top-[-3rem] rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
          @click="closeImageLightbox"
        >
          {{ $t('meetDetailPage.close') }}
        </button>
        <div class="overflow-hidden rounded-3xl border border-white/10 bg-midnight-900/80">
          <img v-if="meet?.coverImage" :src="meet.coverImage" :alt="meet.title" class="max-h-[80vh] w-full object-contain" />
        </div>
      </div>
    </div>

    <div
      v-if="showEditModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeEditModal"
    >
      <div class="w-full max-w-3xl max-h-[90vh] overflow-auto rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Edit Event</p>
            <h3 class="mt-2 font-display text-xl font-semibold text-white">Update your local meet</h3>
          </div>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeEditModal"
          >
            Close
          </button>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Title
            <input
              v-model.trim="editForm.title"
              type="text"
              maxlength="90"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Sunday morning cars & coffee"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Event type
            <select
              v-model="editForm.event_type"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            >
              <option v-for="option in eventTypeOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="block text-sm text-steel-200">
            Max attendees
            <input
              v-model.number="editForm.max_attendees"
              type="number"
              min="1"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="50"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Country
            <input
              v-model.trim="editForm.country"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Romania"
            />
          </label>
          <label class="block text-sm text-steel-200">
            City
            <input
              v-model.trim="editForm.city"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Bucharest"
            />
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Coordinates or Google Maps link
            <div class="mt-2 flex flex-col gap-3 sm:flex-row">
              <input
                v-model.trim="editForm.location_query"
                type="text"
                class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                placeholder="44.4268, 26.1025 or paste a Google Maps link"
              />
              <button
                type="button"
                class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-70"
                :disabled="editBusy"
                @click="resolveEditLocation"
              >
                Use location
              </button>
            </div>
            <p class="mt-2 text-xs text-steel-400">Detected coordinates will drive the embedded map and `Open in Maps`.</p>
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Venue
            <input
              v-model.trim="editForm.venue"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Promenada rooftop parking"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Starts at
            <input
              v-model="editForm.starts_at"
              type="datetime-local"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Ends at
            <input
              v-model="editForm.ends_at"
              type="datetime-local"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            />
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Description
            <textarea
              v-model.trim="editForm.description"
              rows="5"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Who it is for, how the route works, where people should park, and any rules."
            ></textarea>
          </label>
          <div class="sm:col-span-2 rounded-2xl border border-white/10 bg-midnight-900/60 p-4">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-white">Event cover</p>
                <p class="mt-1 text-xs text-steel-400">Upload a new photo or drag to reframe the current one.</p>
              </div>
              <label class="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/30 hover:bg-white/10">
                <input class="hidden" type="file" accept="image/*" @change="handleEditImageUpload" />
                Upload photo
              </label>
            </div>
            <div
              class="mt-4 aspect-[16/8] overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70"
              :class="{ 'cursor-grab active:cursor-grabbing': editForm.coverImage }"
              @pointerdown="startEditPreviewDrag"
            >
              <img
                v-if="editForm.coverImage"
                :src="editForm.coverImage"
                alt="Edit cover preview"
                class="h-full w-full object-cover"
                :style="{ objectPosition: editPreviewPosition }"
                draggable="false"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-steel-400">
                No image uploaded
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap items-center justify-end gap-3">
          <p v-if="editStatusMessage" class="mr-auto text-sm text-steel-300">{{ editStatusMessage }}</p>
          <p v-if="editErrorMessage" class="mr-auto text-sm text-rose-200">{{ editErrorMessage }}</p>
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="closeEditModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="editBusy"
            @click="saveEventEdits"
          >
            {{ editBusy ? 'Saving...' : 'Save changes' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl, getPostImageUrl, refreshReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'
import { emitAppToast } from '@/lib/toast'
import VerifiedBadge from '@/components/VerifiedBadge.vue'

const defaultEditForm = () => ({
  title: '',
  description: '',
  event_type: 'meet',
  location_query: '',
  country: '',
  city: '',
  venue: '',
  latitude: null,
  longitude: null,
  starts_at: '',
  ends_at: '',
  cover_image_url: '',
  cover_image_path: '',
  cover_offset_x: 50,
  cover_offset_y: 50,
  coverImage: '',
  max_attendees: null,
})

export default {
  name: 'LocalMeetDetailView',
  components: {
    VerifiedBadge,
  },
  data() {
    return {
      userId: '',
      meet: null,
      attendees: [],
      comments: [],
      loading: true,
      busy: false,
      commentBusy: false,
      editBusy: false,
      newComment: '',
      errorMessage: '',
      showImageLightbox: false,
      showEditModal: false,
      editForm: defaultEditForm(),
      editPreviewDrag: null,
      editErrorMessage: '',
      editStatusMessage: '',
      eventTypeOptions: [
        { value: 'meet', label: 'Meet' },
        { value: 'drive', label: 'Drive' },
        { value: 'track', label: 'Track Day' },
        { value: 'cars_and_coffee', label: 'Cars & Coffee' },
      ],
    }
  },
  computed: {
    meetId() {
      return this.$route.params.id
    },
    attendeeCountLabel() {
      return Number(this.attendees.length || 0).toLocaleString()
    },
    editPreviewPosition() {
      return `${this.editForm.cover_offset_x}% ${this.editForm.cover_offset_y}%`
    },
  },
  async mounted() {
    await this.refreshView()
  },
  watch: {
    '$route.params.id': async function() {
      await this.refreshView()
    },
  },
  methods: {
    eventTypeLabel(value) {
      const map = {
        meet: 'Meet',
        drive: 'Drive',
        track: 'Track Day',
        cars_and_coffee: 'Cars & Coffee',
      }
      return map[value] || 'Meet'
    },
    eventStatusLabel(value) {
      const map = {
        scheduled: 'Scheduled',
        cancelled: 'Cancelled',
        completed: 'Completed',
      }
      return map[value] || 'Scheduled'
    },
    formatDateTime(value) {
      if (!value) return 'Date TBD'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return 'Date TBD'
      return parsed.toLocaleString([], {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatRelativeTime(value) {
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return ''
      const diffMs = Date.now() - parsed.getTime()
      const diffMinutes = Math.max(1, Math.round(diffMs / 60000))
      if (diffMinutes < 60) return `${diffMinutes}m ago`
      const diffHours = Math.round(diffMinutes / 60)
      if (diffHours < 24) return `${diffHours}h ago`
      const diffDays = Math.round(diffHours / 24)
      return `${diffDays}d ago`
    },
    buildMeetImageUrl({ cover_image_path, cover_image_url }) {
      if (cover_image_path) {
        return getPostImageUrl({ imagePath: cover_image_path, imageUrl: cover_image_url })
      }
      return cover_image_url || ''
    },
    buildMapsUrl(meet) {
      if (Number.isFinite(meet.latitude) && Number.isFinite(meet.longitude)) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${meet.latitude},${meet.longitude}`)}`
      }
      const query = [meet.venue, meet.city, meet.country].filter(Boolean).join(', ')
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || meet.title || 'DRVN event')}`
    },
    buildEmbedMapsUrl(meet) {
      if (Number.isFinite(meet.latitude) && Number.isFinite(meet.longitude)) {
        return `https://www.google.com/maps?q=${encodeURIComponent(`${meet.latitude},${meet.longitude}`)}&output=embed`
      }
      const query = [meet.venue, meet.city, meet.country].filter(Boolean).join(', ')
      return `https://www.google.com/maps?q=${encodeURIComponent(query || meet.title || 'DRVN event')}&output=embed`
    },
    normalizeAttendee(row) {
      const fullName = row.profiles?.full_name || 'DRVN Member'
      return {
        id: row.user_id,
        handle: row.profiles?.handle || 'member',
        fullName,
        avatar: getAvatarUrl({ avatarPath: row.profiles?.avatar_path, avatarUrl: row.profiles?.avatar_url }),
        verified: Boolean(row.profiles?.verified),
        initials: fullName.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('') || 'D',
      }
    },
    normalizeComment(row) {
      const authorName = row.profiles?.full_name || 'DRVN Member'
      return {
        ...row,
        authorName,
        authorHandle: row.profiles?.handle || 'member',
        authorAvatar: getAvatarUrl({ avatarPath: row.profiles?.avatar_path, avatarUrl: row.profiles?.avatar_url }),
        authorVerified: Boolean(row.profiles?.verified),
        authorInitials: authorName.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('') || 'D',
        createdLabel: this.formatRelativeTime(row.created_at),
      }
    },
    normalizeMeet(event, attendeeIds) {
      const hostName = event.host?.full_name || 'DRVN Member'
      return {
        ...event,
        coverImage: this.buildMeetImageUrl(event),
        coverPosition: `${Number.isFinite(event.cover_offset_x) ? event.cover_offset_x : 50}% ${Number.isFinite(event.cover_offset_y) ? event.cover_offset_y : 50}%`,
        eventTypeLabel: this.eventTypeLabel(event.event_type),
        statusLabel: this.eventStatusLabel(event.status),
        startsAtLabel: this.formatDateTime(event.starts_at),
        endsAtLabel: event.ends_at ? `Ends ${this.formatDateTime(event.ends_at)}` : 'No set end time',
        locationLabel: [event.city, event.country].filter(Boolean).join(', ') || 'Location TBD',
        mapsUrl: this.buildMapsUrl(event),
        embedMapsUrl: this.buildEmbedMapsUrl(event),
        hostName,
        hostHandle: event.host?.handle || 'member',
        hostAvatar: getAvatarUrl({ avatarPath: event.host?.avatar_path, avatarUrl: event.host?.avatar_url }),
        hostVerified: Boolean(event.host?.verified),
        hostInitials: hostName.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('') || 'D',
        capacityLabel: Number.isFinite(event.max_attendees) ? Number(event.max_attendees).toLocaleString() : '',
        isGoing: attendeeIds.includes(this.userId),
        isHost: event.host_user_id === this.userId,
      }
    },
    async refreshView() {
      this.loading = true
      this.errorMessage = ''

      try {
        const session = await refreshReadySession()
        this.userId = session?.user?.id || ''

        const meetResult = await runSupabaseQueryWithRetry(() => (
          supabase
            .from('local_meets')
            .select(`
              id, host_user_id, title, description, event_type, country, city, venue, starts_at, ends_at,
              cover_image_url, cover_image_path, cover_offset_x, cover_offset_y, latitude, longitude, max_attendees, status, created_at,
              host:profiles!local_meets_host_user_id_fkey (handle, full_name, avatar_url, avatar_path, verified)
            `)
            .eq('id', this.meetId)
            .maybeSingle()
        ))

        if (meetResult.error || !meetResult.data) {
          this.meet = null
          this.attendees = []
          this.comments = []
          return
        }

        const [attendeesResult, commentsResult] = await Promise.all([
          runSupabaseQueryWithRetry(() => (
            supabase
              .from('local_meet_attendees')
              .select('user_id, profiles!local_meet_attendees_user_id_fkey (handle, full_name, avatar_url, avatar_path, verified)')
              .eq('meet_id', this.meetId)
              .order('created_at', { ascending: true })
          )),
          runSupabaseQueryWithRetry(() => (
            supabase
              .from('local_meet_comments')
              .select('id, meet_id, user_id, content, created_at, profiles!local_meet_comments_user_id_fkey (handle, full_name, avatar_url, avatar_path, verified)')
              .eq('meet_id', this.meetId)
              .order('created_at', { ascending: true })
          )),
        ])

        const attendeeRows = attendeesResult.data || []
        const attendeeIds = attendeeRows.map((row) => row.user_id)
        this.attendees = attendeeRows.map((row) => this.normalizeAttendee(row))
        this.comments = (commentsResult.data || []).map((row) => this.normalizeComment(row))
        this.meet = this.normalizeMeet(meetResult.data, attendeeIds)
      } finally {
        this.loading = false
      }
    },
    openImageLightbox() {
      if (!this.meet?.coverImage) return
      this.showImageLightbox = true
    },
    closeImageLightbox() {
      this.showImageLightbox = false
    },
    openEditModal() {
      if (!this.meet) return
      this.editErrorMessage = ''
      this.editStatusMessage = ''
      this.editForm = {
        title: this.meet.title || '',
        description: this.meet.description || '',
        event_type: this.meet.event_type || 'meet',
        location_query: this.meet.latitude && this.meet.longitude ? `${this.meet.latitude}, ${this.meet.longitude}` : '',
        country: this.meet.country || '',
        city: this.meet.city || '',
        venue: this.meet.venue || '',
        latitude: Number.isFinite(this.meet.latitude) ? this.meet.latitude : null,
        longitude: Number.isFinite(this.meet.longitude) ? this.meet.longitude : null,
        starts_at: this.toDatetimeLocal(this.meet.starts_at),
        ends_at: this.toDatetimeLocal(this.meet.ends_at),
        cover_image_url: this.meet.cover_image_url || '',
        cover_image_path: this.meet.cover_image_path || '',
        cover_offset_x: Number.isFinite(this.meet.cover_offset_x) ? this.meet.cover_offset_x : 50,
        cover_offset_y: Number.isFinite(this.meet.cover_offset_y) ? this.meet.cover_offset_y : 50,
        coverImage: this.meet.coverImage || '',
        max_attendees: Number.isFinite(this.meet.max_attendees) ? this.meet.max_attendees : null,
      }
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
      this.editBusy = false
      this.editPreviewDrag = null
      this.editErrorMessage = ''
      this.editStatusMessage = ''
      this.editForm = defaultEditForm()
    },
    toDatetimeLocal(value) {
      if (!value) return ''
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return ''
      const year = parsed.getFullYear()
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      const day = String(parsed.getDate()).padStart(2, '0')
      const hours = String(parsed.getHours()).padStart(2, '0')
      const minutes = String(parsed.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },
    normalizeDateTimeInput(value) {
      const trimmed = String(value || '').trim()
      if (!trimmed) return ''
      const parsed = new Date(trimmed)
      if (Number.isNaN(parsed.getTime())) return ''
      return parsed.toISOString()
    },
    parseCoordinates(value) {
      const input = String(value || '').trim()
      if (!input) return null

      const simpleMatch = input.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/)
      if (simpleMatch) {
        const latitude = Number(simpleMatch[1])
        const longitude = Number(simpleMatch[2])
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          return { latitude, longitude }
        }
      }

      const atMatch = input.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/)
      if (atMatch) {
        return { latitude: Number(atMatch[1]), longitude: Number(atMatch[2]) }
      }

      const queryMatch = input.match(/[?&]q=(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/)
      if (queryMatch) {
        return { latitude: Number(queryMatch[1]), longitude: Number(queryMatch[2]) }
      }

      return null
    },
    async reverseGeocode(latitude, longitude) {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!response.ok) throw new Error('Location lookup failed.')
      return response.json()
    },
    async resolveEditLocation() {
      this.editErrorMessage = ''
      this.editStatusMessage = ''

      const parsed = this.parseCoordinates(this.editForm.location_query)
      if (!parsed) {
        this.editErrorMessage = 'Enter coordinates like "44.4268, 26.1025" or paste a Google Maps link with coordinates.'
        return
      }

      this.editForm.latitude = parsed.latitude
      this.editForm.longitude = parsed.longitude

      try {
        const data = await this.reverseGeocode(parsed.latitude, parsed.longitude)
        const address = data?.address || {}
        this.editForm.country = this.editForm.country || address.country || ''
        this.editForm.city = this.editForm.city || address.city || address.town || address.village || address.county || ''
        this.editForm.venue = this.editForm.venue || data?.name || address.road || data?.display_name || ''
        this.editStatusMessage = 'Coordinates applied to the event location.'
      } catch (_error) {
        this.editStatusMessage = 'Coordinates saved. Fill the text location manually if needed.'
      }
    },
    startEditPreviewDrag(event) {
      if (!this.editForm.coverImage) return
      event.preventDefault()
      const target = event.currentTarget
      if (!target) return
      if (typeof target.setPointerCapture === 'function') {
        try {
          target.setPointerCapture(event.pointerId)
        } catch (_error) {
          // ignore if capture fails
        }
      }
      const rect = target.getBoundingClientRect()
      this.editPreviewDrag = {
        startX: event.clientX,
        startY: event.clientY,
        startOffsetX: this.editForm.cover_offset_x,
        startOffsetY: this.editForm.cover_offset_y,
        width: rect.width || 1,
        height: rect.height || 1,
      }
      const onMove = (moveEvent) => {
        if (!this.editPreviewDrag) return
        moveEvent.preventDefault()
        const dx = moveEvent.clientX - this.editPreviewDrag.startX
        const dy = moveEvent.clientY - this.editPreviewDrag.startY
        const nextX = this.editPreviewDrag.startOffsetX + (dx / this.editPreviewDrag.width) * 100
        const nextY = this.editPreviewDrag.startOffsetY + (dy / this.editPreviewDrag.height) * 100
        this.editForm.cover_offset_x = Math.max(0, Math.min(100, Math.round(nextX)))
        this.editForm.cover_offset_y = Math.max(0, Math.min(100, Math.round(nextY)))
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        this.editPreviewDrag = null
      }
      window.addEventListener('pointermove', onMove, { passive: false })
      window.addEventListener('pointerup', onUp)
    },
    async convertImageToWebp(file, { maxWidth, quality = 0.74, maxPixels = 1400000 }) {
      const imageObjectUrl = URL.createObjectURL(file)
      const image = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Image decode failed.'))
        img.src = imageObjectUrl
      })

      try {
        const originalWidth = image.width || 1
        const originalHeight = image.height || 1
        const widthScale = Math.min(1, maxWidth / originalWidth)
        const pixelScale = Math.min(1, Math.sqrt(maxPixels / (originalWidth * originalHeight || 1)))
        const scale = Math.min(widthScale, pixelScale)
        const targetWidth = Math.max(1, Math.round(originalWidth * scale))
        const targetHeight = Math.max(1, Math.round(originalHeight * scale))

        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Canvas is not available.')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
        if (!blob) throw new Error('Image conversion failed.')
        return new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' })
      } finally {
        URL.revokeObjectURL(imageObjectUrl)
      }
    },
    async handleEditImageUpload(event) {
      const file = event.target.files?.[0]
      if (!file || !this.userId) return

      this.editErrorMessage = ''
      this.editStatusMessage = ''

      let uploadFile = file
      try {
        uploadFile = await this.convertImageToWebp(file, { maxWidth: 1600 })
      } catch (_error) {
        this.editErrorMessage = 'Photo processing failed. Try a different image.'
        return
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '').replace(/\.[^.]+$/, '.webp')
      const filePath = `local-meets/${this.userId}/${Date.now()}-${safeName}`

      const { error } = await supabase.storage
        .from('posts')
        .upload(filePath, uploadFile, { upsert: false, contentType: 'image/webp', cacheControl: '31536000' })

      if (error) {
        this.editErrorMessage = 'Upload failed. Make sure the "posts" bucket exists.'
        return
      }

      const coverImage = getPostImageUrl({ imagePath: filePath })
      this.editForm.cover_image_path = filePath
      this.editForm.cover_image_url = coverImage
      this.editForm.cover_offset_x = 50
      this.editForm.cover_offset_y = 50
      this.editForm.coverImage = coverImage
      this.editStatusMessage = 'Event cover uploaded.'
      if (event?.target) event.target.value = ''
    },
    async saveEventEdits() {
      if (!this.meet || !this.userId) return

      this.editErrorMessage = ''
      this.editStatusMessage = ''

      const title = String(this.editForm.title || '').trim()
      const country = String(this.editForm.country || '').trim()
      const city = String(this.editForm.city || '').trim()
      const startsAtIso = this.normalizeDateTimeInput(this.editForm.starts_at)
      const endsAtIso = this.normalizeDateTimeInput(this.editForm.ends_at)

      const missingFields = []
      if (!title) missingFields.push('title')
      if (!country) missingFields.push('country')
      if (!city) missingFields.push('city')
      if (!startsAtIso) missingFields.push('start time')

      if (missingFields.length) {
        this.editErrorMessage = `${missingFields.join(', ')} ${missingFields.length === 1 ? 'is' : 'are'} required.`
        return
      }

      if (endsAtIso && new Date(endsAtIso).getTime() < new Date(startsAtIso).getTime()) {
        this.editErrorMessage = 'End time must be after the start time.'
        return
      }

      this.editBusy = true
      const { error } = await supabase
        .from('local_meets')
        .update({
          title: title.slice(0, 90),
          description: this.editForm.description,
          event_type: this.editForm.event_type,
          country,
          city,
          venue: this.editForm.venue,
          latitude: Number.isFinite(this.editForm.latitude) ? this.editForm.latitude : null,
          longitude: Number.isFinite(this.editForm.longitude) ? this.editForm.longitude : null,
          starts_at: startsAtIso,
          ends_at: endsAtIso,
          cover_image_url: this.editForm.cover_image_url,
          cover_image_path: this.editForm.cover_image_path,
          cover_offset_x: this.editForm.cover_offset_x,
          cover_offset_y: this.editForm.cover_offset_y,
          max_attendees: Number.isFinite(this.editForm.max_attendees) ? this.editForm.max_attendees : null,
        })
        .eq('id', this.meet.id)
        .eq('host_user_id', this.userId)
      this.editBusy = false

      if (error) {
        this.editErrorMessage = error.message || 'Unable to save these event changes.'
        return
      }

      emitAppToast({ title: 'Event updated', message: 'Your event changes are now live.', tone: 'success' })
      this.closeEditModal()
      await this.refreshView()
    },
    async joinMeet() {
      if (!this.userId || !this.meet) return
      this.busy = true
      const { error } = await supabase.from('local_meet_attendees').insert({
        meet_id: this.meet.id,
        user_id: this.userId,
        status: 'going',
      })
      this.busy = false
      if (error) {
        this.errorMessage = error.message || 'Unable to join this event.'
        return
      }
      emitAppToast({ title: 'RSVP confirmed', message: 'You are on the list for this event.', tone: 'success' })
      await this.refreshView()
    },
    async leaveMeet() {
      if (!this.userId || !this.meet) return
      this.busy = true
      const { error } = await supabase
        .from('local_meet_attendees')
        .delete()
        .eq('meet_id', this.meet.id)
        .eq('user_id', this.userId)
      this.busy = false
      if (error) {
        this.errorMessage = error.message || 'Unable to cancel your RSVP.'
        return
      }
      emitAppToast({ title: 'RSVP removed', message: 'You are no longer marked as going.', tone: 'info' })
      await this.refreshView()
    },
    async cancelMeet() {
      if (!this.meet || !this.userId) return
      this.busy = true
      const { error } = await supabase
        .from('local_meets')
        .update({ status: 'cancelled' })
        .eq('id', this.meet.id)
        .eq('host_user_id', this.userId)
      this.busy = false
      if (error) {
        this.errorMessage = error.message || 'Unable to cancel this event.'
        return
      }
      emitAppToast({ title: 'Event cancelled', message: 'The meet has been marked as cancelled.', tone: 'info' })
      await this.refreshView()
    },
    async addComment() {
      if (!this.userId || !this.meet || !this.newComment) return
      this.commentBusy = true
      const { error } = await supabase.from('local_meet_comments').insert({
        meet_id: this.meet.id,
        user_id: this.userId,
        content: this.newComment,
      })
      this.commentBusy = false
      if (error) {
        this.errorMessage = error.message || 'Unable to post this comment.'
        return
      }
      this.newComment = ''
      await this.refreshView()
    },
    async deleteComment(commentId) {
      const { error } = await supabase.from('local_meet_comments').delete().eq('id', commentId).eq('user_id', this.userId)
      if (error) {
        this.errorMessage = error.message || 'Unable to delete this comment.'
        return
      }
      await this.refreshView()
    },
  },
}
</script>
